import classNames from "classnames";
import { ReactNode, useState } from "react";
import { MaterialNames } from "@/components/material_symbol_names";
import Icon from "@/components/Icon";
import IconButton from "@/components/IconButton";
import Menu, { MenuItem } from "@/components/Menu";
import { useIsCompactLayout } from "@/hooks/useIsCompactLayout";
import Button from "@/components/Button";

/**
 * Action button configuration for table components (TableHeading and TableControls)
 * @property icon - Icon name for the action
 * @property label - Label for the action
 * @property onClick - Click handler for the action
 * @property testId - Custom test ID for the action button
 * @property variant - Button variant (filled, outlined, or text)
 * @property disabled - Whether the button is disabled
 * @property render - Render function for custom action button
 */
export interface TableAction {
  icon?: MaterialNames;
  label: string;
  onClick: () => void;
  testId?: string;
  variant?: "filled" | "outlined" | "text";
  disabled?: boolean;
  render?: () => ReactNode;
}

/**
 * @deprecated Use TableAction instead. TableDetailsAction will be removed in a future version.
 */
export type TableDetailsAction = TableAction;

/**
 * Props for TableHeading component
 * @property heading - Heading text for the table section
 * @property body - Body text or React element for additional description
 * @property ButtonComps - @deprecated Use actions prop instead for mobile-responsive behavior
 * @property actions - Action buttons with intelligent mobile overflow handling (same as ObjectHeading)
 * @property extendedActions - Additional actions always shown in an overflow menu
 * @property alwaysCollapseSecondAction - Force second action into overflow menu on mobile (useful for long button text)
 * @property isPageHeading - Larger type and `pt-8` for interior reports such as PAT Results — not top-level nav indexes
 * @property headingSize - Title type size when not `isPageHeading`. Use `small` (`text-title-medium-bold`) for nested tables under a parent section heading
 * @property topSpacing - Top padding above the heading block. `default` is `pt-12` when heading or body is present, or `pt-6` for actions-only. Use `compact` for nested tables in dialogs, side sheets, or stacked sections; `none` when a parent already provides section spacing. On a top-level index with a visible tab bar, put create actions on Tabs `actionIcon` and a `pt-6` spacer above the table instead of an actions-only TableHeading.
 * @property collapsible - When true, shows a chevron beside the heading to expand or collapse the table section
 * @property defaultExpanded - Initial expanded state when collapsible and uncontrolled (default `true`)
 * @property expanded - Controlled expanded state when collapsible
 * @property onExpandedChange - Called when the user toggles expand/collapse
 * @property children - Optional table (or other content). When collapsible and collapsed, children stay mounted and are CSS-hidden (same as `Table.collapsed`) so table state is preserved. Actions unmount when collapsed; body text stays visible.
 */
export type TableHeadingTopSpacing = "default" | "compact" | "none";
export type TableHeadingSize = "default" | "small";

export interface TableHeadingProps {
  heading?: string;
  body?: string | React.ReactElement;
  ButtonComps?: (() => JSX.Element)[];
  actions?: TableAction[];
  extendedActions?: MenuItem[];
  alwaysCollapseSecondAction?: boolean;
  isPageHeading?: boolean;
  headingSize?: TableHeadingSize;
  topSpacing?: TableHeadingTopSpacing;
  collapsible?: boolean;
  defaultExpanded?: boolean;
  expanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
  children?: ReactNode;
}

export interface TableHeadingCollapseState {
  expanded: boolean;
  setExpanded: (expanded: boolean) => void;
  headingProps: Pick<TableHeadingProps, "collapsible" | "expanded" | "onExpandedChange">;
}

export function useTableHeadingCollapse(options?: { defaultExpanded?: boolean }): TableHeadingCollapseState {
  const [expanded, setExpanded] = useState(options?.defaultExpanded ?? true);

  return {
    expanded,
    setExpanded,
    headingProps: {
      collapsible: true,
      expanded,
      onExpandedChange: setExpanded
    }
  };
}

/** @deprecated Use TableHeadingProps instead */
export type TableDetailsProps = TableHeadingProps;

export function TableHeading({
  heading,
  body,
  ButtonComps,
  actions = [],
  extendedActions = [],
  alwaysCollapseSecondAction = false,
  isPageHeading,
  headingSize = "default",
  topSpacing = "default",
  collapsible = false,
  defaultExpanded = true,
  expanded: expandedProp,
  onExpandedChange,
  children
}: TableDetailsProps) {
  const { isCompactLayout } = useIsCompactLayout();
  const [uncontrolledExpanded, setUncontrolledExpanded] = useState(defaultExpanded);
  const isExpanded = expandedProp ?? uncontrolledExpanded;

  const setExpanded = (nextExpanded: boolean) => {
    onExpandedChange?.(nextExpanded);
    if (expandedProp === undefined) {
      setUncontrolledExpanded(nextExpanded);
    }
  };

  const hasCopy = Boolean(heading || body);
  const topPaddingClass = isPageHeading
    ? "pt-8"
    : topSpacing === "compact"
      ? "pt-6"
      : topSpacing === "none"
        ? "pt-0"
        : hasCopy
          ? "pt-12"
          : "pt-6";
  const bottomPaddingClass = "pb-6";

  const renderActions = () => {
    if (actions.length === 0 && !extendedActions.length) return null;

    if (isCompactLayout) {
      const firstAction =
        actions.length > 0 ? (
          actions[0].render ? (
            <div>{actions[0].render()}</div>
          ) : (
            <Button
              variant={actions[0].variant || "filled"}
              icon={actions[0].icon}
              onClick={actions[0].onClick}
              disabled={actions[0].disabled}
              testId={actions[0].testId || "table-action-0"}
            >
              {actions[0].label}
            </Button>
          )
        ) : null;

      const secondAction =
        actions.length > 1 && !extendedActions.length && actions.length === 2 && !alwaysCollapseSecondAction ? (
          <>
            {actions[1].render ? (
              <div>{actions[1].render()}</div>
            ) : (
              <Button
                variant={actions[1].variant || "filled"}
                icon={actions[1].icon}
                onClick={actions[1].onClick}
                disabled={actions[1].disabled}
                testId={actions[1].testId || "table-action-1"}
              >
                {actions[1].label}
              </Button>
            )}
          </>
        ) : null;

      const menuActions =
        (actions.length > 1 && (actions.length > 2 || extendedActions.length || alwaysCollapseSecondAction)) ||
        extendedActions.length > 0 ? (
          <Menu
            AnchorComponent={<IconButton name="pending" data-testid="table-actions-menu-button" />}
            menuItems={[
              ...(actions.length > 1 && (actions.length > 2 || extendedActions.length || alwaysCollapseSecondAction)
                ? [
                    {
                      label: actions[1].label,
                      onClick: actions[1].onClick,
                      ...(actions[1].icon ? { iconName: actions[1].icon } : {}),
                      dataTestId: actions[1].testId
                    } as MenuItem
                  ]
                : []),
              ...actions.slice(2).map(
                (action) =>
                  ({
                    label: action.label,
                    onClick: action.onClick,
                    ...(action.icon ? { iconName: action.icon } : {}),
                    dataTestId: action.testId
                  }) as MenuItem
              ),
              ...extendedActions
            ]}
          />
        ) : null;

      return (
        <div className="flex flex-wrap gap-2 items-center">
          {firstAction}
          {secondAction}
          {menuActions}
        </div>
      );
    }

    return (
      <div className="flex items-center gap-3">
        {[...actions].reverse().map((action, index) => {
          const originalIndex = actions.length - 1 - index;
          return action.render ? (
            <div key={originalIndex}>{action.render()}</div>
          ) : (
            <Button
              key={originalIndex}
              variant={action.variant || "filled"}
              icon={action.icon}
              onClick={action.onClick}
              disabled={action.disabled}
              testId={action.testId || `table-action-${originalIndex}`}
            >
              {action.label}
            </Button>
          );
        })}
        {extendedActions.length > 0 && (
          <Menu
            AnchorComponent={<IconButton name="pending" data-testid="table-extended-actions-menu-button" />}
            menuItems={extendedActions}
          />
        )}
      </div>
    );
  };

  const hasActions = actions.length > 0 || extendedActions.length > 0;
  const showActions = hasActions && (!collapsible || isExpanded);
  const showLegacyButtons = Boolean(!hasActions && ButtonComps && (!collapsible || isExpanded));
  const headingClassName = classNames("!m-0 pb-1", {
    "text-headline-large-bold font-serif": isPageHeading,
    "text-title-medium-bold font-serif": !isPageHeading && headingSize === "small",
    "text-title-large-bold font-serif": !isPageHeading && headingSize !== "small"
  });

  if (!hasCopy && !showActions && !showLegacyButtons && !children) {
    return null;
  }

  return (
    <>
      {(hasCopy || showActions || showLegacyButtons) && (
        <div data-testid="table-heading" className={classNames("w-full pl-0", bottomPaddingClass, topPaddingClass)}>
          <div
            className={classNames("w-full lg:w-auto flex flex-col lg:flex-row lg:items-center", {
              "justify-between": hasCopy,
              "justify-end": !hasCopy
            })}
          >
            {hasCopy && (
              <div className="w-full lg:w-auto flex flex-col items-start">
                {heading && (
                  <h2 className={headingClassName}>
                    {collapsible ? (
                      <button
                        type="button"
                        className="inline-flex items-center gap-1 border-0 bg-transparent p-0 font-inherit text-inherit text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sys-brand-primary"
                        aria-label={isExpanded ? `Collapse ${heading}` : `Expand ${heading}`}
                        aria-expanded={isExpanded}
                        onClick={() => setExpanded(!isExpanded)}
                        data-testid="table-heading-collapse-toggle"
                      >
                        {heading}
                        <span aria-hidden="true" className="inline-flex shrink-0">
                          <Icon name={isExpanded ? "expand_less" : "expand_more"} size={20} />
                        </span>
                      </button>
                    ) : (
                      heading
                    )}
                  </h2>
                )}
                {body && <div className="text-body-medium">{body}</div>}
              </div>
            )}

            {showActions && (
              <div
                className={classNames(
                  "w-full lg:w-auto flex flex-wrap gap-3 lg:flex-nowrap justify-start lg:justify-end items-center lg:py-0",
                  {
                    "pt-6 lg:ml-20 lg:pl-10": hasCopy
                  }
                )}
              >
                {renderActions()}
              </div>
            )}

            {showLegacyButtons && (
              <div
                className={classNames(
                  "w-full lg:w-auto flex flex-wrap gap-3 lg:flex-nowrap justify-start lg:justify-end items-center lg:py-0",
                  {
                    "pt-6 lg:ml-20 lg:pl-10": hasCopy
                  }
                )}
              >
                {ButtonComps?.map((ButtonComp, index) => (
                  <ButtonComp key={index} />
                ))}
              </div>
            )}
          </div>
        </div>
      )}
      {children && <div className={classNames({ hidden: collapsible && !isExpanded })}>{children}</div>}
    </>
  );
}

/**
 * @deprecated Use TableHeading instead. TableDetails will be removed in a future version.
 */
export const TableDetails = TableHeading;
